import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import { sopSettingsService, SOPSettings } from '../services/sopSettingsService';

// Define the shape of our app settings
export interface AppSettings {
  showSOPFlaggingFeature: boolean;
  autoGenerateSummaries: boolean;
  // Add other app-wide settings here as needed
}

// Default settings
const defaultSettings: AppSettings = {
  showSOPFlaggingFeature: true, // Default to enabled
  autoGenerateSummaries: true // Default to enabled
};

// Define the context shape
interface AppSettingsContextType {
  appSettings: AppSettings;
  refreshSettings: () => Promise<void>;
  isLoading: boolean;
}

// Create the context with default values
const AppSettingsContext = createContext<AppSettingsContextType>({
  appSettings: defaultSettings,
  refreshSettings: async () => {},
  isLoading: true
});

// Custom hook to use the settings
export const useAppSettings = () => useContext(AppSettingsContext);

interface AppSettingsProviderProps {
  children: ReactNode;
}

export const AppSettingsProvider: React.FC<AppSettingsProviderProps> = ({ children }) => {
  const [appSettings, setAppSettings] = useState<AppSettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);

  // Function to fetch settings from Firebase
  const fetchSettings = async (): Promise<AppSettings> => {
    try {
      // Get SOP settings from the service
      const sopSettings = await sopSettingsService.getSettings();
      
      // Combine with any other settings you might have
      return {
        ...defaultSettings,
        ...sopSettings
      };
    } catch (error) {
      console.error('Error fetching app settings:', error);
      return defaultSettings;
    }
  };

  // Function to refresh settings
  const refreshSettings = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const settings = await fetchSettings();
      setAppSettings(settings);
    } catch (error) {
      console.error('Error refreshing settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load settings on mount
  useEffect(() => {
    refreshSettings();
  }, []);

  return (
    <AppSettingsContext.Provider value={{ appSettings, refreshSettings, isLoading }}>
      {children}
    </AppSettingsContext.Provider>
  );
};

export default AppSettingsProvider;